/* @moldura padrao */
/**
 * page.tsx (viagens) — Lista de viagens (roteiros). Tema AdminUX (Bootstrap).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/auth/auth-context';
import { formatarData } from '@/lib/data';
import { formatarDinheiro } from '@/lib/numero';
import type { TipoViagem, ViagemListItem } from './shared';
import {
  agregaOS,
  badge,
  iconeTipoViagem,
  iconeTransporte,
  rotuloStatusViagem,
  rotuloTipoViagem,
  rotuloTransporte,
  TIPOS_VIAGEM,
  varianteStatusViagem,
} from './shared';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

// Período da viagem em texto curto ("19/06/2026 – 22/06/2026" ou só um lado).
function periodo(de?: string | null, ate?: string | null): string {
  const a = de ? formatarData(de) : '';
  const b = ate ? formatarData(ate) : '';
  if (a && b) return `${a} – ${b}`;
  return a || b || '—';
}

export function ViagensPage() {
  const navigate = useNavigate();
  const { pode } = useAuth();
  // Ver a viagem ≠ ver o custo dela. Sem `viagens.resumo.ver` a coluna "Total
  // gasto" não existe (e a API já mandou o campo nulo).
  const podeCusto = pode('viagens.resumo.ver');
  const [lista, setLista] = useState<ViagemListItem[]>([]);
  const [tipo, setTipo] = useState<TipoViagem | 'todas'>('todas');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  // A cancelada não some: vai para a aba dela, com o porquê (03/11). Na aba
  // principal ela só atrapalhava — era "uma ida para o Rio" que não houve.
  const [vista, setVista] = useState<'abertas' | 'canceladas'>('abertas');
  const qtdCanceladas = lista.filter((v) => v.status === 'cancelada').length;
  const daVista = lista.filter((v) =>
    vista === 'canceladas' ? v.status === 'cancelada' : v.status !== 'cancelada',
  );
  const visiveis = tipo === 'todas' ? daVista : daVista.filter((v) => v.tipo === tipo);

  useEffect(() => {
    let vivo = true;
    setCarregando(true);
    setErro(null);
    api
      .get<ViagemListItem[]>('/viagens')
      .then((r) => vivo && setLista(r))
      .catch((err) => {
        if (!vivo) return;
        setErro(
          err instanceof ApiError
            ? err.message
            : 'Não foi possível carregar as viagens.',
        );
      })
      .finally(() => vivo && setCarregando(false));
    return () => {
      vivo = false;
    };
  }, []);

  return (
    <CustomMolduraPagina
      titulo="Viagens"
      descricao="Roteiros de instalação entre as cidades"
      acoes={
            <button
              className="btn btn-theme"
              onClick={() => navigate('/viagens/nova')}
            >
              <i className="bi bi-plus-lg me-1"></i> Nova viagem
            </button>
      }
    >

      <ul className="nav nav-tabs adminuiux-tabs mt-3 mb-3">
        {([
          ['abertas', 'Viagens', 'bi-suitcase-lg', lista.length - qtdCanceladas],
          ['canceladas', 'Canceladas', 'bi-x-circle', qtdCanceladas],
        ] as const).map(([k, rotulo, icone, qtd]) => (
          <li className="nav-item" key={k}>
            <button
              type="button"
              className={`nav-link ${vista === k ? 'active' : ''}`}
              onClick={() => setVista(k)}
            >
              <i className={`bi ${icone} me-1`}></i>
              {rotulo} <span className="badge text-bg-light ms-1">{qtd}</span>
            </button>
          </li>
        ))}
      </ul>

      {/* Filtro por tipo — a viagem deixou de ser só a dos instaladores */}
      <div className="d-flex flex-wrap gap-2 mb-3">
        <button
          type="button"
          className={`btn btn-sm ${tipo === 'todas' ? 'btn-theme' : 'btn-outline-theme'}`}
          onClick={() => setTipo('todas')}
        >
          Todas <span className="badge text-bg-light ms-1">{daVista.length}</span>
        </button>
        {TIPOS_VIAGEM.map((t) => {
          const qtd = daVista.filter((v) => v.tipo === t.value).length;
          return (
            <button
              key={t.value}
              type="button"
              className={`btn btn-sm ${tipo === t.value ? 'btn-theme' : 'btn-outline-theme'}`}
              onClick={() => setTipo(t.value)}
              title={t.desc}
            >
              <i className={`bi ${t.icon} me-1`}></i>
              {t.label} <span className="badge text-bg-light ms-1">{qtd}</span>
            </button>
          );
        })}
      </div>

      <div className="card adminuiux-card">
        <div className="card-body">
          {carregando ? (
            <div className="d-flex align-items-center justify-content-center gap-2 py-5 text-secondary">
              <span className="spinner-border spinner-border-sm" /> Carregando…
            </div>
          ) : erro ? (
            <div className="py-5 text-center text-danger">{erro}</div>
          ) : (
            <div className="table-responsive">
              <table
                className="table align-middle table-hover mb-0"
                style={{ fontSize: '0.9rem' }}
              >
                <thead>
                  <tr
                    className="text-secondary text-uppercase"
                    style={{ fontSize: '0.75rem' }}
                  >
                    {/* O que some quando a tela aperta, e por que ESTA ordem.
                        No celular sobram Roteiro, Período e Total gasto: quem
                        abre no aparelho quer saber PARA ONDE, QUANDO e QUANTO.
                        O resto se lê abrindo a viagem.
                        Mesma mecânica da lista de OS — ver a convenção visual §8. */}
                    <th>Roteiro</th>
                    <th className="d-none d-lg-table-cell">Tipo</th>
                    <th className="text-nowrap">Período</th>
                    <th className="d-none d-xl-table-cell">Transporte</th>
                    <th className="d-none d-md-table-cell">Status</th>
                    <th className="text-center d-none d-xl-table-cell">OS</th>
                    <th className="d-none d-xxl-table-cell">Equipe</th>
                    {podeCusto && <th className="text-end text-nowrap">Total gasto</th>}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visiveis.map((v) => {
                    const roteiro = [v.origem, ...(v.destinos ?? [])]
                      .filter(Boolean)
                      .join(' → ');
                    return (
                      <tr
                        key={v.id}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/viagens/${v.id}`)}
                      >
                        {/* O roteiro pode ser "Búzios → Rio → Niterói → ...".
                            No celular ele estoura a linha, então trunca com
                            reticências — o nome inteiro fica no `title` e na
                            tela da viagem. */}
                        <td
                          className="fw-semibold text-truncate"
                          style={{ maxWidth: 180 }}
                          title={roteiro}
                        >
                          {roteiro || '—'}
                          {v.encerrada_em && (
                            <i
                              className="bi bi-lock-fill text-secondary ms-2"
                              title="Viagem encerrada"
                              style={{ fontSize: '0.75rem' }}
                            ></i>
                          )}
                          {v.status === 'cancelada' && v.justificativa_cancelamento && (
                            <div
                              className="small text-secondary fw-normal text-truncate"
                              title={v.justificativa_cancelamento}
                            >
                              {v.justificativa_cancelamento}
                            </div>
                          )}
                        </td>
                        <td className="text-nowrap text-secondary d-none d-lg-table-cell">
                          <i className={`bi ${iconeTipoViagem(v.tipo)} me-1`}></i>
                          {rotuloTipoViagem(v.tipo)}
                        </td>
                        <td className="text-secondary text-nowrap">
                          {periodo(v.data_partida, v.data_chegada)}
                        </td>
                        <td className="text-nowrap d-none d-xl-table-cell">
                          <i
                            className={`bi ${iconeTransporte(v.transporte)} me-1`}
                          ></i>
                          {rotuloTransporte(v.transporte)}
                        </td>
                        <td className="d-none d-md-table-cell">
                          <span
                            className={`badge ${badge(varianteStatusViagem(v.status))}`}
                          >
                            {rotuloStatusViagem(v.status)}
                          </span>
                        </td>
                        <td className="text-center d-none d-xl-table-cell">
                          {agregaOS(v.tipo) ? v.qtd_os : '—'}
                        </td>
                        <td
                          className="text-secondary d-none d-xxl-table-cell"
                          title={(v.instaladores ?? []).join(', ')}
                        >
                          {(v.instaladores ?? []).length > 0
                            ? v.instaladores.join(', ')
                            : '—'}
                        </td>
                        {podeCusto && (
                          <td className="text-end fw-semibold text-nowrap">
                            {v.total_gasto ? formatarDinheiro(v.total_gasto) : '—'}
                          </td>
                        )}
                        <td
                          className="text-end"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="btn btn-sm btn-link btn-square"
                            title="Editar"
                            onClick={() => navigate(`/viagens/${v.id}/editar`)}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {visiveis.length === 0 && (
                    <tr>
                      <td
                        colSpan={podeCusto ? 9 : 8}
                        className="py-5 text-center text-secondary"
                      >
                        {vista === 'canceladas' && daVista.length === 0
                          ? 'Nenhuma viagem cancelada.'
                          : lista.length === 0
                            ? 'Nenhuma viagem.'
                            : 'Nenhuma viagem deste tipo.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </CustomMolduraPagina>
  );
}
